import React, { DetailedHTMLProps, HTMLAttributes } from 'react';
import classnames from 'classnames';
import spinner from '../../assets/spinner.svg';
import { caseUtils } from '@codametrix/ui-common';

export type BillingGroupProps = {
  isActive?: boolean;
  showLoading?: boolean;
  cancelled?: boolean;
  groupedCases?: CMxCommonApp.GroupedCases;
  showProcessStatus?: boolean;
  suppressedLinks?: number[];
  viewOnly?: boolean;
} & DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

const getBillingGroup = (
  showProcessStatus: boolean = false,
  suppressedLinks: number[],
  caseRuns?: CMxAPI.CaseRun[]
) => {
  if (!caseRuns?.length) {
    return false;
  }
  const suppressedSet = new Set<number>([...suppressedLinks]);

  const getMiniStatus = (caseRun: CMxAPI.CaseRun, idx: number) => {
    const statusDisplay = caseUtils.deriveStatus(caseRun);
    const description = caseRun.exam_description ?? '';
    const clinicalId = `${caseRun.primary_clinical_identifier.number} (${caseRun.primary_clinical_identifier.issuer})`;

    const mainStatus = showProcessStatus
      ? statusDisplay.processStatus
      : statusDisplay.signifier;

    const styleClasses = [
      'related-group-item',
      'qa-related-group-item',
      statusDisplay.styleClass
    ];

    const inner = (
      <div className="related-group-item-content">
        <div className="info-display">
          <div className="circle"></div>
          {mainStatus}
        </div>
        <div className="descriptive-text ellipsify">{description}</div>
        <div className="clinical-id sensitive-data">#{clinicalId}</div>
      </div>
    );

    const itemClasses = 'qa-related-group-item-line related-group-item-line';

    return (
      <li
        className={classnames(styleClasses)}
        key={`${caseRun.process_instance_id}-${idx}`}>
        {suppressedSet.has(caseRun.case_id) ? (
          <span className={itemClasses}>{inner}</span>
        ) : (
          <a
            className={itemClasses}
            href={`/cmx/cases/${caseRun.case_id}`}
            data-case-id={`${caseRun.case_id}`}
            title={`${description} - ${caseRun.process_status}`}>
            {inner}
          </a>
        )}
      </li>
    );
  };

  const relatedStatuses = caseRuns?.map(getMiniStatus);

  return (
    <div className="relation-grouping">
      <ul className="related-group">{relatedStatuses}</ul>
    </div>
  );
};

const DEFAULT_PROPS: BillingGroupProps = {
  cancelled: false,
  showLoading: false,
  isActive: false,
  showProcessStatus: false,
  suppressedLinks: [],
  viewOnly: false
};

const BillingGroup: React.FunctionComponent<BillingGroupProps> = ({
  cancelled = false,
  groupedCases,
  isActive = false,
  showLoading = false,
  showProcessStatus = false,
  suppressedLinks = [],
  viewOnly = false,
  ...rest
}) => {
  const caseRuns = groupedCases?.caseRuns ?? [];
  const isStandalone = !caseRuns?.length;

  const styleClasses = [
    'billing-group',
    {
      'bg-loaded': !showLoading,
      'bg-with-cases': !!caseRuns?.length,
      'bg-empty': !showLoading && isStandalone,
      'bg-show-loading': showLoading,
      'case-cancelled': cancelled
    }
  ];
  return (
    <div className={classnames(styleClasses)} {...rest}>
      {showLoading && (
        <span className="loading-content">
          <img src={spinner} alt="in progress" className="spinner" />
        </span>
      )}

      {!caseRuns?.length && (
        <span className="empty-content">
          There are no other related cases at this time.
        </span>
      )}

      {!!caseRuns?.length && (
        <div className="billing-group-items">
          {getBillingGroup(showProcessStatus, suppressedLinks, caseRuns)}
        </div>
      )}
    </div>
  );
};

BillingGroup.displayName = 'BillingGroup';

export { BillingGroup };
